import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Award,
  Layers,
  Network,
  ShieldCheck,
  Building2,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const normParts = [
  {
    code: "IEC 62443-2-1",
    role: "Betreiber",
    text: "Managementsystem für OT-Security: Verantwortlichkeiten, Richtlinien, Risikomanagement und kontinuierliche Verbesserung für den Gebäudebetrieb.",
  },
  {
    code: "IEC 62443-2-4",
    role: "Integratoren und Dienstleister",
    text: "Anforderungen an Errichter, GA-Systemhäuser und Wartungsdienstleister: Fernwartung, Patchprozesse, Personal und Projektübergabe.",
  },
  {
    code: "IEC 62443-3-2",
    role: "Planung",
    text: "Risikobeurteilung sowie Zonen- und Conduit-Modell: Aufteilung von Feld, Automations und Managementebene inklusive Target Security Level je Zone.",
  },
  {
    code: "IEC 62443-3-3",
    role: "System",
    text: "Technische Systemanforderungen entlang der sieben Foundational Requirements, von Zugriffskontrolle bis Verfügbarkeit der Automationsfunktionen.",
  },
  {
    code: "IEC 62443-4-1",
    role: "Hersteller",
    text: "Sicherer Produktentwicklungsprozess: Grundlage, um Ausschreibungen für GA-Komponenten prüfbar zu formulieren.",
  },
  {
    code: "IEC 62443-4-2",
    role: "Komponenten",
    text: "Technische Anforderungen an Controller, Gateways, Bedienstationen und Netzwerkkomponenten in der Gebäudeautomation.",
  },
];

const foundationalRequirements = [
  {
    fr: "FR 1",
    title: "Identification and Authentication Control",
    text: "Schluss mit geteilten Servicepasswörtern auf GA-Controllern: eindeutige Identitäten für Betreiber, Errichter und Fernwartung.",
  },
  {
    fr: "FR 2",
    title: "Use Control",
    text: "Rechte nach Rolle: Wer darf parametrieren, wer darf nur beobachten, wer darf Programme in den Automationsstationen ändern?",
  },
  {
    fr: "FR 3",
    title: "System Integrity",
    text: "Schutz von Programmständen, Firmware und Datenpunkten gegen unbemerkte Veränderung, inklusive nachvollziehbarer Änderungsstände.",
  },
  {
    fr: "FR 4",
    title: "Data Confidentiality",
    text: "Vertraulichkeit von Nutzungsdaten, Belegungsprofilen und Zugangsdaten, gerade bei Cloud und Energiemonitoring.",
  },
  {
    fr: "FR 5",
    title: "Restricted Data Flow",
    text: "Zonen und Conduits statt flacher Gebäudenetze: kontrollierte Übergänge zwischen Feldebene, GLT und IT.",
  },
  {
    fr: "FR 6",
    title: "Timely Response to Events",
    text: "Logging und Monitoring, die im Störfall tatsächlich helfen, mit klaren Meldewegen zwischen Facility, IT und Dienstleister.",
  },
  {
    fr: "FR 7",
    title: "Resource Availability",
    text: "Verfügbarkeit der Gebäudefunktionen: Redundanz, Notbetrieb und Wiederanlauf für Heizung, Lüftung, Kälte und Zutritt.",
  },
];

const layers = [
  {
    Icon: Building2,
    title: "Managementebene (GLT, Leitsystem)",
    text: "Server, Visualisierung, Trending, Reporting und Schnittstellen zu IT, ERP und Cloud. Typische Zone mit erhöhtem Target Security Level, weil hier Rechte und Daten zusammenlaufen.",
  },
  {
    Icon: Network,
    title: "Automationsebene (DDC, Gateways)",
    text: "BACnet/IP, BACnet/SC, Modbus TCP und Gatewaylayer zu KNX, LON und M-Bus. Hier entscheidet sich, ob Segmentierung und sichere Kommunikation praktisch funktionieren.",
  },
  {
    Icon: Layers,
    title: "Feldebene (Sensorik, Aktorik)",
    text: "Feldbusse ohne native Sicherheitsmechanismen. Schutz entsteht über physische Sicherung, Segmentierung und kompensierende Maßnahmen im Conduit.",
  },
];

const benefits = [
  "Ein Rahmenwerk für alle Gewerke statt Insellösungen je Hersteller.",
  "Prüfbare Anforderungen für Ausschreibung, Abnahme und Wartungsvertrag.",
  "Gemeinsame Sprache zwischen Betreiber, Facility Management, IT und Errichter.",
  "Direkte Anschlussfähigkeit an NIS-2, BSI INF.13 und ISO 27001.",
  "Security Levels machen Aufwand und Investition begründbar statt beliebig.",
];

const faqs = [
  {
    q: "Warum gilt IEC 62443 als Goldstandard für OT-Security in der Gebäudeautomation?",
    a: "Weil die Normenreihe als einzige durchgängig Betreiber, Integratoren und Hersteller adressiert und technische wie organisatorische Anforderungen zusammenführt. Sie wurde für industrielle Automatisierung entwickelt, ist aber auf Gebäudeautomation und Gebäudeleitsysteme übertragbar, da dort die gleichen Steuerungsarchitekturen, Feldbusse und Lieferkettenmuster vorliegen.",
  },
  {
    q: "Gilt die Norm auch für Gebäudeleitsysteme und Smart Buildings?",
    a: "Ja. Ein Gebäudeleitsystem ist ein industrielles Automatisierungs- und Steuerungssystem im Sinne der Norm: Sensorik, Aktorik, Automationsstationen, Leitrechner, Fernzugriff. Fachgremien und Hersteller aus der Gebäudetechnik nutzen IEC 62443 seit Jahren als Referenz für Smart Buildings, weil es keinen gleichwertigen gebäudespezifischen Standard gibt.",
  },
  {
    q: "Wie verhält sich IEC 62443 zu ISO 27001?",
    a: "ISO 27001 beschreibt ein Managementsystem für Informationssicherheit, bleibt aber technisch unspezifisch für Steuerungstechnik. IEC 62443 ergänzt genau diese Lücke mit Zonenmodell, Security Levels und konkreten System- und Komponentenanforderungen. In der Praxis kombiniere ich beides: ISO 27001 als Dach, IEC 62443 als OT-Tiefgang.",
  },
  {
    q: "Was sind Foundational Requirements?",
    a: "Die sieben Foundational Requirements sind das inhaltliche Rückgrat der Norm: Identifikation und Authentifizierung, Nutzungskontrolle, Systemintegrität, Vertraulichkeit, eingeschränkter Datenfluss, Reaktion auf Ereignisse und Verfügbarkeit. Jede technische Anforderung in IEC 62443-3-3 und -4-2 lässt sich einer dieser sieben Kategorien zuordnen.",
  },
  {
    q: "Welches Security Level ist für ein Bürogebäude realistisch?",
    a: "In den meisten kommerziell genutzten Liegenschaften ist SL 2 ein tragfähiges Ziel, mit einzelnen Zonen auf SL 3, etwa Zutritt, Fernwartungszugang oder Rechenzentrumsversorgung. SL 4 ist Sonderfällen mit staatlichen Bedrohungsakteuren vorbehalten und selten wirtschaftlich begründbar.",
  },
  {
    q: "Muss ich meine Gebäudeautomation zertifizieren lassen?",
    a: "Für Betreiber steht das Betriebsmodell im Vordergrund, nicht das Zertifikat. Zertifizierungen nach IEC 62443-4-1 oder -4-2 sind vor allem für Hersteller und Systemintegratoren relevant und lassen sich als Nachweis in Ausschreibungen fordern.",
  },
  {
    q: "Wo fange ich pragmatisch an?",
    a: "Mit einer Bestandsaufnahme der Anlagen und Kommunikationswege, danach mit dem Zonen- und Conduit-Modell nach IEC 62443-3-2 für eine repräsentative Liegenschaft. Daraus entsteht ein priorisierter Fahrplan, der sich auf weitere Standorte skalieren lässt.",
  },
];

const steps = [
  {
    n: "01",
    title: "Asset- und Kommunikationsinventar",
    text: "Welche Automationsstationen, Gateways, Feldbusse und Fernzugänge existieren wirklich? Ohne dieses Bild bleibt jede Norminterpretation Theorie.",
    to: "/ot-asset-management",
    linkLabel: "Zu OT-Asset-Management",
  },
  {
    n: "02",
    title: "Zonen, Conduits und Target Security Level",
    text: "Risikobeurteilung nach IEC 62443-3-2, Segmentierung der Ebenen und begründete Zielniveaus je Zone.",
    to: "/ot-netzwerkarchitektur",
    linkLabel: "Zur OT-Netzwerkarchitektur",
  },
  {
    n: "03",
    title: "Organisation und Rollen",
    text: "Verantwortlichkeiten zwischen Betreiber, Facility Management, IT und Errichter nach IEC 62443-2-1 und -2-4 festlegen.",
    to: "/ot-organisation",
    linkLabel: "Zur OT-Organisation",
  },
  {
    n: "04",
    title: "Technische Umsetzung und Nachweis",
    text: "Härtung, sichere Protokolle wie BACnet/SC, Fernwartungskonzept, Logging sowie Abnahme- und Wartungsanforderungen.",
    to: "/bacnet-sc-migration",
    linkLabel: "Zur BACnet/SC Migration",
  },
];

const Iec62443Goldstandard = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/iec62443-goldstandard";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline:
          "IEC 62443 als Goldstandard für OT-Security in der Gebäudeautomation",
        description:
          "Warum die Normenreihe IEC 62443 der Goldstandard für die Organisation von OT-Security in Gebäudeautomation und Gebäudeleitsystemen ist: Aufbau, Foundational Requirements, Zonen und Security Levels.",
        author: { "@type": "Person", name: "Tim Bauer" },
        publisher: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        mainEntityOfPage: canonical,
        url: canonical,
        inLanguage: "de",
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://fachsprechpartner.de/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "IEC 62443",
            item: "https://fachsprechpartner.de/iec62443",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Goldstandard Gebäudeautomation",
            item: canonical,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>IEC 62443: Goldstandard für OT-Security in der Gebäudeautomation</title>
        <meta
          name="description"
          content="Warum die Normenreihe IEC 62443 der Goldstandard für OT-Security in Gebäudeautomation und Gebäudeleitsystemen ist: Aufbau, Foundational Requirements, Zonen und Security Levels."
        />
        <meta
          name="keywords"
          content="IEC 62443, Gebäudeautomation, Gebäudeleitsystem, OT-Security, Building Management System, Foundational Requirements, Security Level, Zonen und Conduits"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta
          property="og:title"
          content="IEC 62443: Goldstandard für OT-Security in der Gebäudeautomation"
        />
        <meta
          property="og:description"
          content="Aufbau der Normenreihe, sieben Foundational Requirements, Zonenmodell und Security Levels, übertragen auf Gebäudeautomation und Gebäudeleitsysteme."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="IEC 62443: Goldstandard für OT-Security in der Gebäudeautomation" />
        <meta name="twitter:description" content="Aufbau der Normenreihe, sieben Foundational Requirements, Zonenmodell und Security Levels, übertragen auf Gebäudeautomation und Gebäudeleitsysteme." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <Award className="w-4 h-4" />
                Normenreihe IEC 62443
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                IEC 62443: der Goldstandard für OT-Security in der
                Gebäudeautomation.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Gebäudeautomation und Gebäudeleitsysteme sind Steuerungstechnik,
                keine IT. Die Normenreihe IEC 62443 liefert als einziges
                Rahmenwerk eine durchgängige Organisation von OT-Security über
                Betreiber, Integratoren und Hersteller hinweg. Auf dieser Seite
                zeige ich, wie sich die Norm auf Gebäudeleitsysteme übertragen
                lässt und welche Bausteine wirklich zählen.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Sparring anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/iec62443"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Leistungen nach IEC 62443
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Warum Goldstandard */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Warum ausgerechnet diese Normenreihe
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                IEC 62443 wurde für industrielle Automatisierungs- und
                Steuerungssysteme entwickelt. Genau diese Architektur findet
                sich in jedem größeren Gebäude wieder: Feldgeräte, Automations
                stationen, ein Leitsystem, Fernwartung durch Errichter und eine
                lange Lieferkette. Deshalb greifen Hersteller und Fachgremien
                der Gebäudetechnik seit Jahren auf diese Reihe zurück, statt
                einen eigenen Standard zu erfinden.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Der entscheidende Unterschied zu reinen IT-Standards: IEC 62443
                denkt Verfügbarkeit und Anlagensicherheit zuerst und verteilt
                Verantwortung explizit auf Betreiber, Integrator und Hersteller.
                Das macht sie in der Gebäudeautomation praktisch anwendbar.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Aufbau der Normenreihe */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Aufbau der Normenreihe
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Die relevanten Teile für Gebäudeautomation und Gebäudeleitsysteme,
              sortiert nach Rolle im Projekt und Betrieb.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {normParts.map(({ code, role, text }) => (
                <div
                  key={code}
                  className="p-6 rounded-2xl border border-border bg-card"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-heading font-bold text-lg">
                      {code}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-accent/10 text-accent">
                      {role}
                    </span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Foundational Requirements */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Die sieben Foundational Requirements in der GA
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Jede technische Anforderung der Norm lässt sich auf eine dieser
              sieben Grundkategorien zurückführen. So sieht das übersetzt auf
              Gebäudeautomation aus.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {foundationalRequirements.map(({ fr, title, text }) => (
                <div
                  key={fr}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-accent">
                    {fr}
                  </span>
                  <h3 className="text-lg font-heading font-semibold mt-1 mb-2">
                    {title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ebenen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Mapping auf die Ebenen des Gebäudeleitsystems
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Zonen und Conduits entstehen nicht am Reißbrett, sondern entlang
              der real vorhandenen Automationsebenen.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {layers.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security Levels */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Security Levels als Investitionslogik
            </h2>
            <div className="space-y-4">
              {[
                {
                  sl: "SL 1",
                  text: "Schutz gegen zufällige Fehlbedienung und ungewollte Störungen im laufenden Betrieb.",
                },
                {
                  sl: "SL 2",
                  text: "Schutz gegen einfache, absichtliche Angriffe mit geringen Mitteln. Realistisches Zielniveau für die meisten Liegenschaften.",
                },
                {
                  sl: "SL 3",
                  text: "Schutz gegen gezielte Angriffe mit OT-spezifischem Know-how. Sinnvoll für kritische Zonen wie Zutritt, Fernwartung oder RZ-Versorgung.",
                },
                {
                  sl: "SL 4",
                  text: "Schutz gegen Akteure mit hohen Ressourcen und staatlicher Motivation. Nur in Sonderfällen wirtschaftlich begründbar.",
                },
              ].map(({ sl, text }) => (
                <div
                  key={sl}
                  className="flex gap-4 p-5 rounded-xl border border-border bg-card"
                >
                  <span className="font-heading font-bold text-accent w-14 flex-shrink-0">
                    {sl}
                  </span>
                  <span className="text-muted-foreground leading-relaxed">
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vorgehen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              In vier Schritten zum Betriebsmodell
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              So wird aus dem Goldstandard eine Umsetzung, die den Betrieb nicht
              ausbremst.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {steps.map(({ n, title, text, to, linkLabel }) => (
                <div
                  key={n}
                  className="p-6 rounded-2xl border border-border bg-card"
                >
                  <span className="font-heading font-bold text-3xl text-accent/40">
                    {n}
                  </span>
                  <h3 className="text-xl font-heading font-semibold mt-2 mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {text}
                  </p>
                  <Link
                    to={to}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
                  >
                    {linkLabel} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Abgrenzung */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Verhältnis zu NIS-2, BSI Grundschutz und ISO 27001
            </h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                <span className="text-foreground font-semibold">NIS-2</span>{" "}
                formuliert Pflichten auf hoher Ebene und sagt nichts darüber,
                wie eine Automationsstation abgesichert wird. IEC 62443 liefert
                genau diese Umsetzungsebene und macht Nachweise gegenüber
                Behörden und Auditoren belastbar.{" "}
                <Link to="/nis2" className="text-accent hover:underline">
                  Mehr zu NIS-2
                </Link>
                .
              </p>
              <p>
                <span className="text-foreground font-semibold">
                  BSI IT-Grundschutz
                </span>{" "}
                konkretisiert mit den Bausteinen INF.13 und INF.14 die
                Gebäudeperspektive, bleibt bei Steuerungstechnik jedoch grob.
                Die Kombination aus Grundschutzbaustein und IEC 62443 deckt
                sowohl Prüfpfad als auch technische Tiefe ab.
              </p>
              <p>
                <span className="text-foreground font-semibold">ISO 27001</span>{" "}
                bildet das Dach für Informationssicherheit. IEC 62443 füllt
                darunter die OT-spezifischen Anforderungen, die ein reines
                ISMS nicht abdeckt.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Den Goldstandard in Ihre Liegenschaft bringen
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Ob Bestandsgebäude, Neubau oder Portfolio: Wir klären gemeinsam,
              welche Teile der Normenreihe für Sie zählen und wo der Einstieg am
              meisten bringt.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Iec62443Goldstandard;
